import React, { useEffect, useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  TextField,
  InputAdornment,
  Box,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { fetchDonorById } from "../../redux/donorSlice";
import useAutoFocus from "../AutoTab";
import { fetchDropdownOptions } from "../../redux/dropdownoptionsSlice";
import { logger } from "../../utils/logger";
import { useDebouncedLogger } from "../../hooks/useDebouncedLogger";
import DonationLabelPreview from "../DonationLabelPreview";

const DonationForm = forwardRef(({ initialData, onSave }, ref) => {
  const [donation, setDonation] = useState({
    donorId: "",
    transactionId: "",
    firstName: "",
    lastName: "",
    mobile1: "",
    mobile2: "",
    city: "",
    bookNo: "",
    receiptNo: "",
    childReceiptNo: "",
    receiptDate: dayjs(),
    payMode: "Cash",
    amount: "",
    upiId: "",
    bankName: "",
    instrumentNo: "",
    instrumentDate: null,
    payPurpose: "Annadhanam",
    tributeName: "",
    sharma: "",
    poojaDate: null,
    tamilMonths: "",
    actualReceiptNo: "",
    nachathiram: "",
    thithi: "",
    utsavam: "",
    donationRemarks: "",
    complete: false,
  });
  const { selectedDonor } = useSelector((state) => state.donor || {});
  const receiptYear = donation.receiptDate
    ? dayjs(donation.receiptDate).year()
    : dayjs().year();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const donations = useSelector((state) => state.donation.donations);
  const { role } = useSelector((state) => state.auth || "user");
  const { user } = useSelector((state) => state.auth);
  const { formRef, handleKeyDown } = useAutoFocus();
  const isEditMode = !!initialData?._id;
  const { data = {} } = useSelector((state) => state.dropdownOptions);
  const logFieldChange = useDebouncedLogger();

  const normalizeReceiptNo = (value) => {
    const year = donation.receiptDate
      ? dayjs(donation.receiptDate).year().toString()
      : "";

    if (value && year && value.startsWith(year)) {
      return value.slice(year.length);
    }
    return value;
  };

  useEffect(() => {
    if (donation.donorId) {
      dispatch(fetchDonorById(donation.donorId));
    }
  }, [donation.donorId, dispatch]);

  useEffect(() => {
    if (!data?.paymode?.length) {
      dispatch(fetchDropdownOptions("paymode"));
    }
    if (!data?.paypurpose?.length) {
      dispatch(fetchDropdownOptions("paypurpose"));
    }
    if (!data?.utsavam?.length) {
      dispatch(fetchDropdownOptions("utsavam"));
    }
    if (!data?.tamilmonths?.length) {
      dispatch(fetchDropdownOptions("tamilmonths"));
    }
    if (!data?.nachathiram?.length) {
      dispatch(fetchDropdownOptions("nachathiram"));
    }
    if (!data?.thithi?.length) {
      dispatch(fetchDropdownOptions("thithi"));
    }
  }, [
    dispatch,
    data?.paymode,
    data?.paypurpose,
    data?.utsavam,
    data?.tamilmonths,
    data?.nachathiram,
    data?.thithi,
  ]);
  // Normalize payMode case to match dropdown option casing
  useEffect(() => {
    if (donation.payMode && data?.paymode?.length) {
      const matchedOption = data.paymode.find(
        (opt) =>
          opt.value.toLowerCase() === donation.payMode.toLowerCase(),
      );
      if (matchedOption && matchedOption.value !== donation.payMode) {
        setDonation((prev) => ({ ...prev, payMode: matchedOption.value }));
      }
    }
  }, [data?.paymode, donation.payMode]);
  // useEffect(() => {
  //   if (initialData) {
  //     setDonation((prev) => ({
  //       ...prev,
  //       ...initialData,
  //       donorId: initialData.donorId || prev.donorId,
  //       firstName: initialData.firstName || prev.firstName,
  //       lastName: initialData.lastName || prev.lastName,
  //       mobile1: initialData.mobile1
  //         ? initialData.mobile1.slice(-10)
  //         : prev.mobile1,
  //       mobile2: initialData.mobile2
  //         ? initialData.mobile2.slice(-10)
  //         : prev.mobile2,
  //       city: initialData.city || prev.city,
  //       receiptNo: initialData.receiptNo
  //         ? normalizeReceiptNo(String(initialData.receiptNo))
  //         : "",
  //       childReceiptNo: initialData.childReceiptNo || prev.childReceiptNo || "",
  //       // Only fall back to "today" for a brand-new donation (no _id yet).
  //       // For an existing donation with a missing/invalid receiptDate,
  //       // silently defaulting to today would mask the fact that the date
  //       // is actually missing, and could get saved back over the real
  //       // (empty) value the next time any other field is edited.
  //       receiptDate:
  //         initialData.receiptDate &&
  //         typeof initialData.receiptDate === "string" &&
  //         initialData.receiptDate.trim() !== "" &&
  //         dayjs(initialData.receiptDate).isValid()
  //           ? dayjs(initialData.receiptDate)
  //           : initialData._id
  //             ? null
  //             : dayjs(),
  //       instrumentDate:
  //         initialData.instrumentDate &&
  //         typeof initialData.instrumentDate === "string" &&
  //         initialData.instrumentDate.trim() !== "" &&
  //         dayjs(initialData.instrumentDate).isValid()
  //           ? dayjs(initialData.instrumentDate).toISOString()
  //           : null,
  //       poojaDate:
  //         initialData.poojaDate &&
  //         typeof initialData.poojaDate === "string" &&
  //         initialData.poojaDate.trim() !== "" &&
  //         dayjs(initialData.poojaDate).isValid()
  //           ? dayjs(initialData.poojaDate).format("DD/MMM")
  //           : null,
  //     }));
  //   }
  // }, [initialData]);

  // Set default childReceiptNo based on payPurpose
  useEffect(() => {
  if (initialData) {
    setDonation((prev) => {
      const rawReceiptNo = initialData.receiptNo
        ? String(initialData.receiptNo)
        : "";

      // Year-a initialData.receiptDate la irundhu NERADI edukkanum,
      // component state (donation.receiptDate) andha nேrام் stale-a
      // irukkும், adhை use pannaka koodathu
      const yearFromInitialData =
        initialData.receiptDate &&
        typeof initialData.receiptDate === "string" &&
        initialData.receiptDate.trim() !== "" &&
        dayjs(initialData.receiptDate).isValid()
          ? dayjs(initialData.receiptDate).year().toString()
          : "";

      const normalizedReceiptNo =
        rawReceiptNo &&
        yearFromInitialData &&
        rawReceiptNo.startsWith(yearFromInitialData)
          ? rawReceiptNo.slice(yearFromInitialData.length)
          : rawReceiptNo;

      return {
        ...prev,
        ...initialData,
        donorId: initialData.donorId || prev.donorId,
        firstName: initialData.firstName || prev.firstName,
        lastName: initialData.lastName || prev.lastName,
        mobile1: initialData.mobile1
          ? initialData.mobile1.slice(-10)
          : prev.mobile1,
        mobile2: initialData.mobile2
          ? initialData.mobile2.slice(-10)
          : prev.mobile2,
        city: initialData.city || prev.city,
        receiptNo: normalizedReceiptNo, // ✅ correct value, stale state illa
        childReceiptNo:
          initialData.childReceiptNo || prev.childReceiptNo || "",
        receiptDate:
          initialData.receiptDate &&
          typeof initialData.receiptDate === "string" &&
          initialData.receiptDate.trim() !== "" &&
          dayjs(initialData.receiptDate).isValid()
            ? dayjs(initialData.receiptDate)
            : initialData._id
              ? null
              : dayjs(),
        instrumentDate:
          initialData.instrumentDate &&
          typeof initialData.instrumentDate === "string" &&
          initialData.instrumentDate.trim() !== "" &&
          dayjs(initialData.instrumentDate).isValid()
            ? dayjs(initialData.instrumentDate).toISOString()
            : null,
        poojaDate:
          initialData.poojaDate &&
          typeof initialData.poojaDate === "string" &&
          initialData.poojaDate.trim() !== "" &&
          dayjs(initialData.poojaDate).isValid()
            ? dayjs(initialData.poojaDate).format("DD/MMM")
            : null,
      };
    });
  }
}, [initialData]);
  useEffect(() => {
    if (
      donation.payPurpose === "NithiyaPoojaFund" ||
      donation.payPurpose === "UtsavAnnadhanaFund"
    ) {
      if (!donation.childReceiptNo || donation.childReceiptNo === "") {
        setDonation((prev) => ({
          ...prev,
          childReceiptNo: "1",
        }));
      }
    }
  }, [donation.payPurpose]);

  useEffect(() => {
    if (isEditMode) {
      logger.info("Donation Form Opened - Edit Mode", {
        page: "Donations",
        donorId: initialData?.donorId,
        transactionId: initialData?._id,
        username: user?.username,
        role: user?.role,
      });
    } else {
      logger.info("Donation Form Opened - Add Mode", {
        page: "Donations",
        username: user?.username,
        role: user?.role,
      });
    }
    return () => {
      logger.info("Donation Form Closed", {
        page: "Donations",
        donorId: donation.donorId,
        transactionId: donation.transactionId || null,
        username: user?.username,
        role: user?.role,
      });
    };
  }, []);

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        width: 250,
      },
    },
  };

    const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const oldValue = donation[name];
    const newValue = type === "checkbox" ? checked : value;

    // Donor ID & Book No — digits only
    if (name === "donorId" || name === "bookNo") {
      const numericValue = value.replace(/\D/g, "");
      setDonation((prev) => ({ ...prev, [name]: numericValue }));
      const error = validateField(name, numericValue);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      if (oldValue !== numericValue) {
        logFieldChange(name, "Donation Form Field Changed", {
          page: "Donations",
          donorId: donation.donorId,
          transactionId: donation.transactionId || null,
          username: user?.username,
          role: user?.role,
          field: name,
          oldValue: oldValue,
          newValue: numericValue,
        });
      }
      return;
    }

  
    if (
      name === "payMode" &&
      ["Cancelled", "Not Used", "Porul Varavu"].includes(value)
    ) {
      setDonation((prev) => ({
        ...prev,
        [name]: value,
        amount: "0",
        bankName: value !== "Cheque" ? "" : prev.bankName,
        instrumentNo: value !== "Cheque" ? "" : prev.instrumentNo,
        instrumentDate: value !== "Cheque" ? null : prev.instrumentDate,
        upiId: value !== "UPI" ? "" : prev.upiId,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, amount: "" }));
      return;
    }

    // Handle payPurpose change - set default childReceiptNo for specific payPurpose values
    if (name === "payPurpose") {
      if (value === "NithiyaPoojaFund" || value === "UtsavAnnadhanaFund") {
        setDonation((prev) => ({
          ...prev,
          [name]: newValue,
          childReceiptNo: "1", // Set default child receipt number to 1
        }));
      } else {
        setDonation((prev) => ({
          ...prev,
          [name]: newValue,
          childReceiptNo: "", // Clear child receipt number for other payPurpose values
        }));
      }
    } else {
      // Prevent negative amounts
      if (name === "amount" && Number(value) < 0) {
        setDonation((prev) => ({ ...prev, amount: "0" }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          amount: "Amount cannot be negative.",
        }));
        return;
      }

      // ✅ Update donation with correct value type
      setDonation((prev) => ({ ...prev, [name]: newValue }));
    }

    // Field validation
    const error = validateField(name, newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    // Log field changes — debounced per field so rapid keystrokes collapse
    // into one "final value" log line ~700ms after the user stops typing,
    // instead of one log (and one network request) per character.
    if (oldValue !== newValue) {
      logFieldChange(name, "Donation Form Field Changed", {
        page: "Donations",
        donorId: donation.donorId,
        transactionId: donation.transactionId || null,
        username: user?.username,
        role: user?.role,
        field: name,
        oldValue: oldValue,
        newValue: newValue,
      });
    }
  };
  const handleDateChange = (name, newDate) => {
    if (!newDate || !dayjs(newDate).isValid()) {
      setDonation((prev) => ({ ...prev, [name]: null }));
      return;
    }

    if (name === "poojaDate") {
      // store as DD/MMM
      const formatted = dayjs(newDate).format("DD/MMM");
      setDonation((prev) => ({ ...prev, [name]: formatted }));
    } else {
      // for other dates, keep full ISO
      setDonation((prev) => ({
        ...prev,
        [name]: dayjs(newDate).toISOString(),
      }));
    }
  };

  const validateField = (name, value) => {
    let error = "";
    let isDuplicate = null;

    switch (name) {
      case "firstName":
        if (!value) {
          error = "First name is required.";
        }
        break;

      case "mobile1":
        if (/\d/.test(value) && !/^\d{10}$/.test(value)) {
          error = "Mobile number must be 10 digits.";
        }
        break;

      case "mobile2":
        if (/\d/.test(value) && !/^\d{10}$/.test(value)) {
          error = "Mobile number must be 10 digits.";
        }
        break;

      case "amount":
        if (value === "" || value === null || value === undefined) {
          error = "Please Enter Amount.";
        } else if (!/^\d+$/.test(value)) {
          error = "Amount must contain only digits.";
        } else if (Number(value) < 0) {
          error = "Amount cannot be negative.";
        }
        break;

      case "payMode":
        if (!value) {
          error = "Payment mode is required.";
        }
        break;

      // case "bankName":
      //   if (
      //     donation.payMode === "Cheque" &&
      //     (!value || typeof value !== "string" || !value.trim())
      //   ) {
      //     error = "Bank name is required.";
      //   } else if (
      //     donation.payMode === "Cheque" &&
      //     value &&
      //     !/^[A-Za-z\s]+$/.test(value)
      //   ) {
      //     error = "Bank name should contain only letters.";
      //   }
      //   break;

      // case "instrumentNo":
      //   if (donation.payMode === "Cheque") {
      //     if (!value || typeof value !== "string" || !value.trim()) {
      //       error = "Instrument number is required.";
      //     } else if (!/^\d{6}$/.test(value.trim())) {
      //       error = "Instrument number must be exactly 6 digits.";
      //     }
      //   }
      //   break;
case "bankName":
  if (
    donation.payMode === "Cheque" &&
    (!value || typeof value !== "string" || !value.trim())
  ) {
    error = "Bank name is required.";
  } else if (
    donation.payMode === "Cheque" &&
    value &&
    !/^[A-Za-z0-9\s.&()\-/,]+$/.test(value) // CHANGED: allow common symbols too
  ) {
    error = "Bank name contains invalid characters.";
  }
  break;

case "instrumentNo":
  if (donation.payMode === "Cheque") {
    if (!value || typeof value !== "string" || !value.trim()) {
      error = "Instrument number is required.";
    } else if (!/^\d+$/.test(value.trim())) {
      // CHANGED: any number of digits, no fixed length
      error = "Instrument number must contain only digits.";
    }
  }
  break;
      case "instrumentDate":
        if (
          donation.payMode === "Cheque" &&
          (!value || (typeof value === "string" && !value.trim()))
        ) {
          error = "Instrument date is required.";
        }
        break;
case "upiId":
  if (donation.payMode === "UPI") {
    if (!value || !value.trim()) {
      error = "UPI-TNXId is required.";
    }
    //  else if (!/^\d{12}$/.test(value.trim())) {
    //   error = "UPI-TNXId must be a 12 digit number.";
    // }
  }
  break;

      case "bookNo":
        if (!value) {
          error = "Please Enter Book No.";
        }
        break;

      case "receiptNo": {
        const receiptYear = donation.receiptDate
          ? dayjs(donation.receiptDate).year().toString()
          : "";

        const numericValue = normalizeReceiptNo(value);

        if (!numericValue) {
          error = "Please Enter Receipt No.";
          break;
        }

        if (
          !/^\d+$/.test(numericValue) &&
          donation.payMode !== "Porul varavu"
        ) {
          error = "Receipt No must contain only digits.";
          break;
        }

        // ---------- DUPLICATE CHECK (BACKEND FORMAT) ----------
        if (!donation.childReceiptNo) {
          const prefixedReceiptNo = `${receiptYear}${numericValue}`;

          const isDuplicate = donations?.some(
            (item) =>
              String(item.receiptNo) === prefixedReceiptNo &&
              String(item.transactionId) !== String(donation.transactionId),
          );

          if (isDuplicate) {
            error = "This Receipt No already exists. Please check!";
            break;
          }
        }

        // ---------- BOOK NO VALIDATION ----------
        const receiptNoNum = Number(numericValue);
        const bookNo = Number(donation.bookNo);

        if (bookNo && receiptNoNum) {
          const expectedBookNo = Math.ceil(receiptNoNum / 25);
          if (expectedBookNo !== bookNo) {
            error = `Receipt No doesn't match with Book No. Expected Book No is ${expectedBookNo}.`;
          }
        }
        break;
      }
      case "childReceiptNo":
        if (value) {
          setErrors((prev) => ({ ...prev, receiptNo: "" }));
          const receiptYear = donation.receiptDate
            ? dayjs(donation.receiptDate).year().toString()
            : "";
          const receiptNoStr = donation.receiptNo
            ? donation.receiptNo.toString()
            : "";
          const prefix = receiptNoStr.substring(0, 4);
          var prefixedReceiptNo = receiptNoStr; // default to current receiptNo
          if (prefix !== receiptYear) {
            prefixedReceiptNo = `${receiptYear}${receiptNoStr}`;
          } else {
            prefixedReceiptNo = receiptNoStr;
          }
          // Check for duplicate combination of receiptNo + childReceiptNo
          const isDuplicateCombo = donations?.some(
            (item) =>
              String(item.receiptNo) === String(prefixedReceiptNo) && // match parent receipt with prefix
              String(item.childReceiptNo) === String(value) && // match child number
              String(item.transactionId) !== String(donation.transactionId),
          );

          if (isDuplicateCombo) {
            error = `The combination of Receipt No ${donation.receiptNo}-${value} already exists.`;
          }
        } else {
          // For other payPurpose values, check if PayPurpose fields are filled
          const poojaFields = [
            "tributeName",
            "sharma",
            "poojaDate",
            "tamilMonths",
            "nachathiram",
            "thithi",
            "utsavam",
          ];

          const anyFieldFilled = poojaFields.some(
            (field) =>
              donation[field] && donation[field].toString().trim() !== "",
          );

          if (
            anyFieldFilled &&
            (!value || value.trim() === "") &&
            (donation.payPurpose === "NithiyaPoojaFund" ||
              donation.payPurpose === "UtsavAnnadhanaFund")
          ) {
            error =
              "Child Receipt No is mandatory when pay purpose-related fields are filled.";
          }
        }
        break;
      // case "receiptDate":
      //   if (!value) {
      //     error = "Please update Receipt date.";
      //   } else {
      //     // Check if this donation has a childReceiptNo (means it's a child)
      //     if (donation.childReceiptNo && donation.receiptNo) {
      //       // Find the parent donation with same receiptNo and no childReceiptNo
      //       const parentDonation = donations?.find(
      //         (item) =>
      //           item.receiptNo === donation.receiptNo &&
      //           !item.childReceiptNo &&
      //           item.transactionId !== donation.transactionId,
      //       );
      //       if (parentDonation) {
      //         const parentDate = dayjs(parentDonation.receiptDate).format(
      //           "DD/MM/YYYY",
      //         );
      //         const currentDate = dayjs(value).format("DD/MM/YYYY");

      //         if (parentDate !== currentDate) {
      //           error = `Receipt Date must match the parent Receipt No (${parentDonation.receiptNo}) date: ${parentDate}.`;
      //         }
      //       }
      //     }
      //   }
      //   break;
      case "receiptDate":
        if (!value) {
          error = "Please update Receipt date.";
        } else {
          if (donation.childReceiptNo && donation.receiptNo) {
            const selectedYear = dayjs(value).year().toString();
            const receiptNoStr = donation.receiptNo.toString();
            const prefixYear = receiptNoStr.substring(0, 4);

            let parentReceiptNo;
            // ✅ If prefix matches → use as is
            if (prefixYear === selectedYear) {
              parentReceiptNo = receiptNoStr;
            } else {
              parentReceiptNo = selectedYear + receiptNoStr;
            }
            const parentDonation = donations?.find(
              (item) =>
                String(item.receiptNo) === String(parentReceiptNo) && // match parent receipt with computed parentReceiptNo
                item.transactionId !== donation.transactionId, // exclude current record
            );

            if (parentDonation) {
              const parentDate = dayjs(parentDonation.receiptDate).format(
                "DD/MM/YYYY",
              );
              const currentDate = dayjs(value).format("DD/MM/YYYY");

              if (parentDate !== currentDate) {
                error = `Receipt Date must match the parent Receipt No (${parentDonation.receiptNo}) date: ${parentDate}.`;
              }
            } else {
              console.warn(
                `⚠️ Parent not found for receiptNo="${parentReceiptNo}". Possible reasons:\n1. Parent donation doesn't exist\n2. Format mismatch in receiptNo\n3. Parent has a childReceiptNo value\n4. Current record is the parent itself`,
              );
            }
          }
        }
        break;

      case "payPurpose":
        // Validate that childReceiptNo is provided for NithiyaPoojaFund and UtsavAnnadhanaFund
        if (
          (value === "NithiyaPoojaFund" || value === "UtsavAnnadhanaFund") &&
          (!donation.childReceiptNo || donation.childReceiptNo.trim() === "")
        ) {
          // Set error on childReceiptNo field instead of payPurpose
          setErrors((prevErrors) => ({
            ...prevErrors,
            childReceiptNo:
              "Child Receipt No is mandatory for NithiyaPoojaFund and UtsavAnnadhanaFund.",
          }));
        } else if (
          value !== "NithiyaPoojaFund" &&
          value !== "UtsavAnnadhanaFund"
        ) {
          // Clear childReceiptNo error for other payPurpose values
          setErrors((prevErrors) => ({
            ...prevErrors,
            childReceiptNo: "",
          }));
        }
        break;

      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: donation.payMode === "UPI" || name !== "upiId" ? error : "",
    }));
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    logger.info("Donation Form Submission Started", {
      page: "Donations",
      username: user?.username,
      role: user?.role,
      donorId: donation.donorId,
    });
    let validationErrors = {};
    Object.keys(donation).forEach((field) => {
      const error = validateField(field, donation[field]);
      if (error) {
        validationErrors[field] = error;
      }
    });

    // Additional validation for payPurpose and childReceiptNo relationship
    if (
      (donation.payPurpose === "NithiyaPoojaFund" ||
        donation.payPurpose === "UtsavAnnadhanaFund") &&
      (!donation.childReceiptNo || donation.childReceiptNo.trim() === "")
    ) {
      validationErrors.childReceiptNo =
        "Child Receipt No is mandatory for NithiyaPoojaFund and UtsavAnnadhanaFund.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      logger.error("Donation Form Submission Validation Errors", {
        page: "Donations",
        donorId: donation.donorId,
        transactionId: donation.transactionId || null,
        username: user?.username,
        role: user?.role,
        errors: validationErrors,
      });
      return;
    }
    try {
      if (isEditMode) {
        logger.info("Donation Form Submitted - Edit Donation", {
          page: "Donations",
          donorId: donation.donorId,
          transactionId: donation.transactionId,
          username: user?.username,
          role: user?.role,
          updatedDonation: donation,
        });
      } else {
        logger.info("Donation Form Submitted - New Donation Added", {
          page: "Donations",
          donorId: donation.donorId,
          newTransactionId: donation.transactionId,
          username: user?.username,
          role: user?.role,
          donationData: donation,
        });
      }
      onSave(donation);
      if (!isEditMode) {
        navigate("/dashboard", {
          state: { selectedMenu: "donations", donorId: donation.donorId },
        });
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  React.useImperativeHandle(ref, () => ({
    submitForm: () => handleSubmit({ preventDefault: () => {} }),
  }));

  return (
    <Grid container spacing={3}>
      {/* Left Side — Donation Form */}
      <Grid item xs={12} md={8}>
        <Box
          ref={formRef}
          component="form"
          onSubmit={handleSubmit}
          sx={{ marginTop: 2 }}
        >
          <Grid container spacing={2}>
            {/*Donor id */}
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label=" Donor ID"
                name="donorId"
                value={donation.donorId}
                onChange={handleChange}
                disabled={
                  initialData
                    ? role === "admin1" ||
                      role === "admin2" ||
                      role === "superadmin" ||
                      role === "editor2"
                    : false
                }
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "black",
                    WebkitTextFillColor: "black",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Grid>

            {/* First Name */}
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={donation.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={donation.lastName}
                onChange={handleChange}
              />
            </Grid>

            {/* Mobile Number 1 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Number 1"
                name="mobile1"
                value={donation.mobile1}
                onChange={handleChange}
                error={!!errors.mobile1}
                helperText={errors.mobile1}
              />
            </Grid>

            {/* Mobile Number 2 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Number 2"
                name="mobile2"
                value={donation.mobile2}
                onChange={handleChange}
                error={!!errors.mobile2}
                helperText={errors.mobile2}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={donation.city}
                onChange={handleChange}
              />
            </Grid>

            {/* Book No */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Book No"
                name="bookNo"
                value={donation.bookNo}
                onChange={handleChange}
                error={!!errors.bookNo}
                helperText={errors.bookNo}
                autoFocus
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
            </Grid>

            {/* Receipt No */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Receipt No"
                name="receiptNo"
                value={donation.receiptNo}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {receiptYear}
                    </InputAdornment>
                  ),
                }}
                onChange={handleChange}
                error={!!errors.receiptNo}
                helperText={errors.receiptNo}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
            </Grid>

            {/* Parent Receipt Number */}
            {(donation.payPurpose === "UtsavAnnadhanaFund" ||
              donation.payPurpose === "NithiyaPoojaFund") && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Child Receipt No"
                  name="childReceiptNo"
                  value={donation.childReceiptNo}
                  onChange={handleChange}
                  error={!!errors.childReceiptNo}
                  helperText={errors.childReceiptNo}
                />
              </Grid>
            )}

            {/* Receipt Date */}
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Receipt Date"
                  value={
                    donation.receiptDate &&
                    dayjs(donation.receiptDate).isValid()
                      ? dayjs(donation.receiptDate)
                      : null
                  }
                  format="DD/MM/YYYY"
                  maxDate={dayjs()}
                  onChange={(newDate) =>
                    handleDateChange("receiptDate", newDate)
                  }
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  slotProps={{
                    textField: {
                      inputProps: { readOnly: true },
                      error: !!errors.receiptDate,
                      helperText: errors.receiptDate,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Pay Mode */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Pay Mode</InputLabel>
                <Select
                  label="Pay Mode"
                  name="payMode"
                  value={donation.payMode}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                >
                  {(data.paymode || []).map((opt) => (
                    <MenuItem key={opt._id} value={opt.value}>
                      {opt.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                value={donation.amount}
                onChange={handleChange}
                error={!!errors.amount}
                helperText={errors.amount}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
            </Grid>

            {/* Condition for cheque Pay mode */}
            {donation.payMode === "Cheque" && (
              <>
                {/* Bank Name */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    name="bankName"
                    value={donation.bankName}
                    error={!!errors.bankName}
                    helperText={errors.bankName}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* Instrument Number */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Instrument Number"
                    name="instrumentNo"
                    value={donation.instrumentNo}
                    error={!!errors.instrumentNo}
                    helperText={errors.instrumentNo}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* Instrument Date */}

                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Instrument Date"
                      value={
                        donation.instrumentDate
                          ? dayjs(donation.instrumentDate)
                          : null
                      }
                      format="DD/MM/YYYY"
                      maxDate={dayjs()}
                      onChange={(newDate) =>
                        handleDateChange("instrumentDate", newDate)
                      }
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                      slotProps={{
                        textField: {
                          inputProps: { readOnly: true },
                          error: !!errors.instrumentDate,
                          helperText: errors.instrumentDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
            {(donation.payMode === "UPI" ||
              donation.payMode === "GPay" ||
              donation.payMode === "PhonePay") && (
              <>
                {/* UPI-TNXId */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="UPI-TNXId"
                    name="upiId"
                    value={donation.upiId}
                    onChange={handleChange}
                    error={!!errors.upiId}
                    helperText={errors.upiId}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>
              </>
            )}

            {/* Pay Purpose */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Pay Purpose</InputLabel>
                <Select
                  label="Pay Purpose"
                  name="payPurpose"
                  value={donation.payPurpose}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                >
                  {(data.paypurpose || []).map((opt) => (
                    <MenuItem key={opt._id} value={opt.value}>
                      {opt.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Sankalpa Name"
                name="tributeName"
                value={donation.tributeName}
                onChange={handleChange}
                error={!!errors.tributeName}
                helperText={errors.tributeName}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Sharma"
                name="sharma"
                value={donation.sharma}
                onChange={handleChange}
                error={!!errors.sharma}
                helperText={errors.sharma}
              />
            </Grid>
           
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Pooja Date"
                  value={
                    donation.poojaDate &&
                    dayjs(donation.poojaDate, "DD/MMM").isValid()
                      ? dayjs(donation.poojaDate, "DD/MMM")
                      : null
                  }
                  format="DD/MMM"
                  onChange={(newDate) => handleDateChange("poojaDate", newDate)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  slotProps={{
                    textField: {
                      inputProps: { readOnly: true },
                      error: !!errors.poojaDate,
                      helperText: errors.poojaDate,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

          
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel> Tamil Months</InputLabel>
                <Select
                  label="Tamil Months"
                  name="tamilMonths"
                  value={donation.tamilMonths || ""}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">Select</MenuItem>
                  {(data.tamilmonths || []).map((opt) => (
                    <MenuItem key={opt._id} value={opt.tamilValue}>
                      {opt.tamilValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

           
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Actual Receipt No"
                name="actualReceiptNo"
                value={donation.actualReceiptNo || ""}
                onChange={handleChange}
              />
            </Grid>

           
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Stars</InputLabel>
                <Select
                  label="Stars"
                  name="nachathiram"
                  value={donation.nachathiram}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">Select</MenuItem>
                  {(data.nachathiram || []).map((opt) => (
                    <MenuItem key={opt._id} value={opt.tamilValue}>
                      {opt.tamilValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Thithi</InputLabel>
                <Select
                  label="Thithigai"
                  name="thithi"
                  value={donation.thithi}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">Select</MenuItem>
                  {(data.thithi || []).map((opt) => (
                    <MenuItem key={opt._id} value={opt.tamilValue}>
                      {opt.tamilValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {(donation.payPurpose === "UtsavAnnadhanaFund" ||
              donation.payPurpose === "NithiyaPoojaFund") && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Utsavam</InputLabel>
                  <Select
                    label="Utsavam"
                    name="utsavam"
                    value={donation.utsavam}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {(data.utsavam || []).map((opt) => (
                      <MenuItem key={opt._id} value={opt.tamilValue}>
                        {opt.tamilValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

          
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Donation Remarks"
                name="donationRemarks"
                value={donation.donationRemarks}
                onChange={handleChange}
              />
            </Grid>

           
            {(donation.payPurpose === "NithiyaPoojaFund" ||
              donation.payPurpose === "UtsavAnnadhanaFund") && (
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="complete"
                      checked={donation.complete}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                    />
                  }
                  label="NPF Label"
                />
              </Grid>
            )} */}

            {/* Sankalpa Name - UtsavAnnadhanam & NithiyaPooja only */}
            {(donation.payPurpose === "UtsavAnnadhanaFund" ||
              donation.payPurpose === "NithiyaPoojaFund") && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Sankalpa Name"
                  name="tributeName"
                  value={donation.tributeName}
                  onChange={handleChange}
                  error={!!errors.tributeName}
                  helperText={errors.tributeName}
                />
              </Grid>
            )}

            {/* Sharma - UtsavAnnadhanam & NithiyaPooja only */}
            {(donation.payPurpose === "UtsavAnnadhanaFund" ||
              donation.payPurpose === "NithiyaPoojaFund") && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Sharma"
                  name="sharma"
                  value={donation.sharma}
                  onChange={handleChange}
                  error={!!errors.sharma}
                  helperText={errors.sharma}
                />
              </Grid>
            )}

            {/* Pooja Date - NithiyaPooja only */}
            {donation.payPurpose === "NithiyaPoojaFund" && (
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Pooja Date"
                    value={
                      donation.poojaDate &&
                      dayjs(donation.poojaDate, "DD/MMM").isValid()
                        ? dayjs(donation.poojaDate, "DD/MMM")
                        : null
                    }
                    format="DD/MMM"
                    onChange={(newDate) =>
                      handleDateChange("poojaDate", newDate)
                    }
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    slotProps={{
                      textField: {
                        inputProps: { readOnly: true },
                        error: !!errors.poojaDate,
                        helperText: errors.poojaDate,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            )}

            {/* Tamil Months - NithiyaPooja only */}
            {donation.payPurpose === "NithiyaPoojaFund" && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Tamil Months</InputLabel>
                  <Select
                    label="Tamil Months"
                    name="tamilMonths"
                    value={donation.tamilMonths || ""}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {(data.tamilmonths || []).map((opt) => (
                      <MenuItem key={opt._id} value={opt.tamilValue}>
                        {opt.tamilValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Actual Receipt No - UtsavAnnadhanam & NithiyaPooja only */}
            {(donation.payPurpose === "UtsavAnnadhanaFund" ||
              donation.payPurpose === "NithiyaPoojaFund") && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Actual Receipt No"
                  name="actualReceiptNo"
                  value={donation.actualReceiptNo || ""}
                  onChange={handleChange}
                />
              </Grid>
            )}

            {/* Stars - NithiyaPooja only */}
            {donation.payPurpose === "NithiyaPoojaFund" && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Stars</InputLabel>
                  <Select
                    label="Stars"
                    name="nachathiram"
                    value={donation.nachathiram}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {(data.nachathiram || []).map((opt) => (
                      <MenuItem key={opt._id} value={opt.tamilValue}>
                        {opt.tamilValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Thithi - NithiyaPooja only */}
            {donation.payPurpose === "NithiyaPoojaFund" && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Thithi</InputLabel>
                  <Select
                    label="Thithigai"
                    name="thithi"
                    value={donation.thithi}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {(data.thithi || []).map((opt) => (
                      <MenuItem key={opt._id} value={opt.tamilValue}>
                        {opt.tamilValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Utsavam - UtsavAnnadhanam only */}
            {donation.payPurpose === "UtsavAnnadhanaFund" && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Utsavam</InputLabel>
                  <Select
                    label="Utsavam"
                    name="utsavam"
                    value={donation.utsavam}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {(data.utsavam || []).map((opt) => (
                      <MenuItem key={opt._id} value={opt.tamilValue}>
                        {opt.tamilValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Donation Remarks - ALL pay purposes */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Donation Remarks"
                name="donationRemarks"
                value={donation.donationRemarks}
                onChange={handleChange}
              />
            </Grid>

            {/* Complete / NPF Label - NithiyaPooja & UtsavAnnadhanam only */}
            {(donation.payPurpose === "NithiyaPoojaFund" ||
              donation.payPurpose === "UtsavAnnadhanaFund") && (
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="complete"
                      checked={donation.complete}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                    />
                  }
                  label="NPF Label"
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>

      {/* Right Side — NPF/UAF Label Preview */}
      <Grid item xs={12} md={4}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Label Preview
        </Typography>
        <DonationLabelPreview data={donation} donorData={selectedDonor} />
      </Grid>
    </Grid>
  );
});

export default DonationForm;
